// src/pages/Login.js — 餐廳營運台 登入頁
//
// 改自 frontend-public/src/pages/Login.js,但:
//   - 拿掉 Google 登入 + 註冊/返回首頁 (餐廳帳號由我們開通,booking 無 /register、無 landing)
//   - 純 email/密碼,打共用 toy-app backend /api/auth/login
//   - 成功後 window.location.href = '/dashboard' (full reload → Layout 取 planSettings)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

// 與 agent 站共用同一組 Google OAuth client；後端 /api/auth/google 以 email 比對既有帳號 → 綁到同一帳號
const googleClientId =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '964776120413-ei5cuq1l62t8sgid029vbh691kqi3rda.apps.googleusercontent.com';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // 已登入 → 直接進營運台
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      try {
        JSON.parse(storedUser);
        window.location.href = '/dashboard';
      } catch {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('companyId');
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ email, password }),
        mode: 'cors',
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.user && data.user.companyId) {
          localStorage.setItem('companyId', data.user.companyId.toString());
        }

        setSnackbarSeverity('success');
        setSnackbarMessage('登入成功！正在跳轉...');
        setOpenSnackbar(true);
        window.dispatchEvent(new Event('authChange'));

        // full reload → Layout 會從 /api/auth/me 補 planSettings
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 800);
      } else {
        setError(data.message || '登入失敗,請檢查您的帳號和密碼');
        setSnackbarSeverity('error');
        setSnackbarMessage(data.message || '登入失敗,請檢查您的帳號和密碼');
        setOpenSnackbar(true);
      }
    } catch (err) {
      setError('登入時出現錯誤,請稍後再試');
      setSnackbarSeverity('error');
      setSnackbarMessage('登入時出現錯誤,請稍後再試');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    if (!credentialResponse?.credential) return;
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token: credentialResponse.credential }),
        mode: 'cors',
        credentials: 'include',
      });
      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.user && data.user.companyId) {
          localStorage.setItem('companyId', data.user.companyId.toString());
        }
        setSnackbarSeverity('success');
        setSnackbarMessage('Google 登入成功！正在跳轉...');
        setOpenSnackbar(true);
        window.dispatchEvent(new Event('authChange'));
        // full reload → Layout 從 /api/auth/me 補 planSettings
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 800);
      } else {
        // booking 帳號由後台開通,無自助註冊 → 提示聯繫開通
        const msg =
          response.status === 404
            ? '此 Google 帳號尚未開通餐廳營運台,請聯繫我們開通'
            : data.message || 'Google 登入失敗';
        setError(msg);
        setSnackbarSeverity('error');
        setSnackbarMessage(msg);
        setOpenSnackbar(true);
      }
    } catch (err) {
      setError('Google 登入時發生錯誤,請稍後再試');
      setSnackbarSeverity('error');
      setSnackbarMessage('Google 登入時發生錯誤,請稍後再試');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={3}
          sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          {/* Logo / 標題 */}
          <RestaurantMenuIcon sx={{ fontSize: 40, color: '#145da0', mb: 1 }} />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#145da0' }}>
            Rebaz 餐廳營運台
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            語音・線上訂位・排隊,一個後台
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="電子郵件"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="密碼"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isLoading}
                />
              }
              label="記住帳號"
              sx={{ mb: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                py: 1.5,
                bgcolor: '#145da0',
                '&:hover': { bgcolor: '#125390' },
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : '登入'}
            </Button>
          </Box>

          {/* Google 登入 — 後端以 email 綁到既有開通帳號（同一帳號，不會分裂） */}
          <Box sx={{ width: '100%', mt: 3 }}>
            <Divider sx={{ mb: 2, fontSize: 13, color: 'text.secondary' }}>或</Divider>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <GoogleLogin
                onSuccess={handleGoogleLoginSuccess}
                onError={() => {
                  setSnackbarSeverity('error');
                  setSnackbarMessage('Google 登入失敗,請稍後再試');
                  setOpenSnackbar(true);
                }}
                text="signin_with"
                shape="rectangular"
                width="280"
              />
            </Box>
          </Box>
        </Paper>

        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          sx={{ display: 'block', mt: 3 }}
        >
          捷思博科技股份有限公司
        </Typography>
      </Container>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
    </GoogleOAuthProvider>
  );
};

export default Login;
